import React from 'react';
import PropTypes from 'prop-types';
import { delay } from 'lodash';
import Cookies from 'cookies-js';

import { withMainControls } from '@eb/eds-structure';
import { TYPE_ERROR } from '@eb/eds-notification';

import { EVENT_COOKIE, EVENT_SAVE, EVENT_SAVE_ERROR } from '../constants';

import { AlertChunky } from '@eb/eds-iconography';
import { CheckChunky } from '@eb/eds-iconography';

/*
This is an HoC that should be utilized with applications that implement
save/unsave functionality for events. It should be wrapped at a high level
such that isAuthenticated and an onSave can be passed as props.

In the case of a logged out save and then follow up redirect back, it will
parse the cookie and attempt to call the passed in save action and then display
a notification that the event was successfully saved.

Additionally it passed down a prop to display an already configured success or
error notification for the event save action.
*/
const withEventSaveNotification = (Component) =>
    withMainControls(
        class LoggedOutEventSaveRedirect extends React.PureComponent {
            static propTypes = {
                /*
            Function that will be called if user is redirected back
            and we need to trigger the save action
            */
                onSaveEvent: PropTypes.func.isRequired,
                /*
            Needed to determine if possible to call save event
            callback
            */
                isAuthenticated: PropTypes.bool.isRequired,
            };

            componentDidMount() {
                const { isAuthenticated, onSaveEvent } = this.props;
                const eventIdFromCookie = Cookies.get(EVENT_COOKIE);

                if (eventIdFromCookie && isAuthenticated) {
                    onSaveEvent(eventIdFromCookie, true, {}, false)
                        .then((status) => {
                            this._handleShowSaveNotification(status, true);
                        })
                        .catch(() => {
                            this._handleShowSaveNotification('error', true);
                        });

                    Cookies.expire(EVENT_COOKIE);
                }
            }

            /*
        @param status {string} oneOf(['success', 'error'])
        @param isRedirect {bool}
        */
            _handleShowSaveNotification = (status, isRedirect = false) => {
                const {
                    addMainNotification,
                    hideMainNotification,
                } = this.props;
                let notificationInfo = {
                    type: TYPE_ERROR,
                    children: EVENT_SAVE_ERROR,
                    iconType: <AlertChunky />,
                    isNotificationSticky: true,
                };

                if (status === 'success') {
                    notificationInfo = {
                        children: EVENT_SAVE,
                        iconType: <CheckChunky />,
                        isNotificationSticky: true,
                    };
                }

                //Only show notification if an error, or user save
                //was successful after log-in redirect
                if (
                    status !== 'success' ||
                    (status === 'success' && isRedirect)
                ) {
                    addMainNotification(notificationInfo);

                    delay(hideMainNotification, 7000);
                }
            };

            render() {
                return <Component {...this.props} />;
            }
        },
    );

export default withEventSaveNotification;
